import React, { useState, useEffect, useRef } from "react";
import QRCode from "qrcode";
import * as api from "../api";
import { C, R, F } from "../theme";
import { Icon } from "../icons";
import { Btn, Card, Chip, Callout, Ref, SectionHeader } from "./UI";

function MFASetup({ user }) {
  const [status,     setStatus]     = useState(null);
  const [step,       setStep]       = useState("idle");
  const [qrUrl,      setQrUrl]      = useState("");
  const [secret,     setSecret]     = useState("");
  const [code,       setCode]       = useState("");
  const [msg,        setMsg]        = useState("");
  const [error,      setError]      = useState("");
  const [loading,    setLoading]    = useState(false);
  const qrCanvasRef = useRef(null);

  useEffect(() => {
    api.adminGetMFAStatus().then(d => setStatus(d.mfa_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (qrUrl && qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, qrUrl, { width: 200, margin: 2, color: { dark: C.navy, light: "#ffffff" } });
    }
  }, [qrUrl]);

  async function startSetup() {
    setLoading(true); setError(""); setMsg("");
    try {
      const data = await api.adminSetupMFA();
      setSecret(data.secret); setQrUrl(data.otpauth_url); setStep("scan");
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  async function confirmSetup(e) {
    e.preventDefault(); setLoading(true); setError("");
    try {
      await api.adminConfirmMFA(secret, code.replace(/\s/g, ""));
      setStatus(true); setStep("idle"); setCode(""); setMsg("MFA activé.");
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  async function disableMFA(e) {
    e.preventDefault(); setLoading(true); setError("");
    try {
      await api.adminDisableMFA(code.replace(/\s/g, ""));
      setStatus(false); setStep("idle"); setCode(""); setMsg("MFA désactivé.");
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  // Style commun aux champs de code à 6 chiffres
  const codeFieldStyle = {
    padding: "12px 11px",
    fontSize: F.size["3xl"],
    fontWeight: F.weight.semi,
    textAlign: "center",
    letterSpacing: 8,
    fontVariantNumeric: "tabular-nums",
    marginBottom: 14,
  };

  return (
    <div style={{ maxWidth: 560, margin: "8px auto 0" }}>
      <SectionHeader
        icon="shield"
        title="Sécurité MFA"
        subtitle="Double authentification par application mobile"
      />

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{
            width: 38, height: 38, borderRadius: R.md, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: status ? C.successSoft : C.surfaceAlt,
            border: `1px solid ${status ? C.successBorder : C.border}`,
            color: status ? C.success : C.inkMuted,
          }}>
            <Icon name={status ? "lock" : "unlock"} size={18} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink }}>
              {status === null ? "Chargement…" : status ? "MFA activé" : "MFA désactivé"}
            </div>
            <div style={{ marginTop: 5 }}>
              <Chip tone={status ? "success" : "danger"} icon={status ? "check" : "close"} small>
                {status ? "Actif" : "Inactif"}
              </Chip>
            </div>
          </div>
        </div>
      </Card>

      {msg && <Callout tone="success" style={{ marginBottom: 14 }}>{msg}</Callout>}
      {error && <Callout tone="danger" style={{ marginBottom: 14 }}>{error}</Callout>}

      {step === "idle" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!status && (
            <Btn icon="lock" onClick={startSetup} disabled={loading} loading={loading}>
              {loading ? "Génération…" : "Activer le MFA"}
            </Btn>
          )}
          {status && (
            <Btn variant="danger" icon="unlock" onClick={() => { setStep("disable"); setCode(""); setError(""); }}>
              Désactiver le MFA
            </Btn>
          )}
        </div>
      )}

      {step === "scan" && (
        <Card>
          <h3 style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
            fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink,
          }}>
            <Icon name="qr" size={17} color={C.inkMuted} />
            Scannez le QR code
          </h3>

          <Callout icon="mobile" style={{ marginBottom: 14 }}>
            Ouvrez votre application d'authentification et scannez ce code.
          </Callout>

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <canvas
              ref={qrCanvasRef}
              style={{ borderRadius: R.md, border: `1px solid ${C.border}`, padding: 6, background: C.surface }}
            />
          </div>

          <details style={{ marginBottom: 14 }}>
            <summary style={{ cursor: "pointer", fontSize: F.size.base, color: C.inkMuted }}>
              Clé de secours (saisie manuelle)
            </summary>
            <div style={{
              marginTop: 8, padding: "10px 12px",
              background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md,
            }}>
              <Ref style={{ wordBreak: "break-all", letterSpacing: 1 }}>{secret}</Ref>
            </div>
          </details>

          <Btn fullWidth iconRight="forward" onClick={() => { setStep("confirm"); setCode(""); setError(""); }}>
            Suivant
          </Btn>
        </Card>
      )}

      {step === "confirm" && (
        <Card>
          <h3 style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
            fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink,
          }}>
            <Icon name="check" size={17} color={C.inkMuted} />
            Confirmez le code
          </h3>
          <form onSubmit={confirmSetup}>
            <input
              className="field"
              value={code}
              onChange={e => setCode(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="000000" maxLength={6} autoFocus inputMode="numeric"
              aria-label="Code à 6 chiffres"
              style={codeFieldStyle}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Btn type="submit" style={{ flex: 1 }} disabled={loading || code.length !== 6} loading={loading}>
                {loading ? "…" : "Activer"}
              </Btn>
              <Btn variant="light" icon="back" onClick={() => { setStep("scan"); setError(""); }}>
                Retour
              </Btn>
            </div>
          </form>
        </Card>
      )}

      {step === "disable" && (
        <Card>
          <h3 style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
            fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.danger,
          }}>
            <Icon name="warning" size={17} />
            Désactiver le MFA
          </h3>

          <Callout tone="warning" style={{ marginBottom: 14 }}>
            La désactivation réduit la protection du compte. Entrez un code valide pour confirmer.
          </Callout>

          <form onSubmit={disableMFA}>
            <input
              className="field"
              value={code}
              onChange={e => setCode(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="000000" maxLength={6} autoFocus inputMode="numeric"
              aria-label="Code à 6 chiffres"
              style={codeFieldStyle}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="danger" type="submit" style={{ flex: 1 }} disabled={loading || code.length !== 6} loading={loading}>
                {loading ? "…" : "Confirmer"}
              </Btn>
              <Btn variant="light" onClick={() => { setStep("idle"); setError(""); }}>
                Annuler
              </Btn>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

export default MFASetup;
